<template>
  <div class="system-trace-panel">
    <div class="timeline-body">
      <div v-if="loading" class="loading-mask">
        <a-spin />
      </div>
      <div class="timeline-list" :class="{ 'timeline-list--empty': !loading && !rawData.length }">
        <div
          v-for="(c, i) in displayList"
          :key="c.sourceId || i"
          class="timeline-box"
          :class="{ offLine: c.sourceName === '门诊病历' }"
        >
          <div>
            <div class="line" v-if="c.diagnosisText">
              <span class="label">就诊诊断：</span>
              <span class="value">{{ c.diagnosisText || '--' }}</span>
            </div>
            <div class="line" v-if="c.westernDiagnoses">
              <span class="label">西医诊断：</span>
              <span class="value">{{ c.westernDiagnoses || '--' }}</span>
            </div>
            <div class="line" v-if="c.chineseDiagnoses">
              <span class="label">中医症型：</span>
              <span class="value">{{ c.chineseDiagnoses || '--' }}</span>
            </div>
            <div class="line" v-if="c.medicineText">
              <span class="label">处方药品：</span>
              <span class="value">{{ c.medicineText || '--' }}</span>
            </div>
            <div class="line" v-if="c.prescriptionTime">
              <span class="label">开方时间：</span>
              <span class="value">{{ c.prescriptionTime || '--' }}</span>
            </div>
          </div>
          <div class="tag" :class="{ 'tag-offLine': c.sourceName === '门诊病历' }">
            {{ c.sourceName || '--' }}
          </div>
          <div class="timeline-main">
            <div v-for="(item, index) in c.cardList" :key="item.id || index" class="timeline-item">
              <div class="card">
                <div class="meta">
                  <span class="round">{{ item.cardName || '--' }}</span>
                </div>
                <div class="date">
                  {{ item.evaluateTime || '--' }}
                </div>
                <div
                  class="status"
                  :style="{
                    color: getStatusColor(item.resultText),
                    backgroundColor: getStatusBackgroundColor(item.resultText)
                  }"
                >
                  {{ item.resultText || '--' }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <MoreButton v-if="showMoreButton" @expand="expanded = true" />
        <a-empty v-if="!loading && !rawData.length" :image="emptyImg" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, ref, watch, unref } from 'vue'
import { getFollowUpTreatmentOverview } from '@/api/patientFile'
import emptyImg from '@/assets/img/empty.png'
import MoreButton from './MoreButton.vue'
import { useExpandableList } from '../../hooks/useExpandableList'

const props = defineProps({
  ucDoctorId: {
    type: [String, Number],
    default: ''
  },
  ucPatientId: {
    type: [String, Number],
    default: ''
  }
})

const loading = ref(false)
const rawData = ref([])
const DEFAULT_VISIBLE_COUNT = 2

const { expanded, displayList, showMoreButton, resetExpand } = useExpandableList(
  rawData,
  DEFAULT_VISIBLE_COUNT
)

// 状态颜色映射
const statusColorMap = {
  // 疗效跟踪
  无回复: '#A8A8A8',
  不好: '#A8A8A8',
  一般: '#FC8801',
  良好: '#02C5A2',
  很好: '#02C5A2',
  // 疗效反馈
  未填写: '#A8A8A8',
  失访: '#A8A8A8',
  用药中: '#A8A8A8',
  无效: '#FF4A4A',
  有效: '#02C5A2',
  治愈: '#02C5A2'
}

// 状态背景颜色映射
const statusBackgroundColorMap = {
  // 疗效跟踪
  无回复: '#F6F6F6',
  不好: '#F6F6F6',
  一般: '#FFF3E6',
  良好: '#E6FFF3',
  很好: '#E6FFF3',
  // 疗效反馈
  未填写: '#F6F6F6',
  失访: '#F6F6F6',
  用药中: '#F6F6F6',
  无效: '#FFF3E6',
  有效: '#E6F9F6',
  治愈: '#E6F9F6'
}

async function fetchTimeline() {
  const doctorId = unref(props.ucDoctorId)
  const patientId = unref(props.ucPatientId)
  if (!doctorId || !patientId) {
    rawData.value = []
    resetExpand()
    return
  }
  loading.value = true
  resetExpand()
  try {
    const result = await getFollowUpTreatmentOverview({
      ucDoctorId: doctorId,
      ucPatientId: patientId
    })
    rawData.value = Array.isArray(result) ? result : []
  } catch {
    rawData.value = []
  } finally {
    loading.value = false
  }
}

watch(
  () => [props.ucDoctorId, props.ucPatientId],
  () => {
    fetchTimeline()
  },
  { immediate: true }
)

const getStatusColor = status => statusColorMap[status] || '#A8A8A8'
const getStatusBackgroundColor = status => statusBackgroundColorMap[status] || '#F6F6F6'
</script>

<style lang="scss" scoped>
.system-trace-panel {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.timeline-body {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.loading-mask {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.65);
  z-index: 3;
}

.timeline-list {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;

  &.timeline-list--empty {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
.timeline-box {
  border-radius: 8px;
  background: #f5f7ff;
  padding: 12px;
  margin-bottom: 12px;
  position: relative;
  .line {
    font-size: 14px;
    line-height: 28px;
    color: #58718f;
    margin-bottom: 6px;

    .value {
      color: #031d4a;
      font-weight: 500;
    }
  }
  &.offLine {
    background: #f5fbff;
  }
  .tag {
    position: absolute;
    top: 0;
    right: 0;
    background: #e5eaff;
    border-radius: 0 8px 0 8px;
    color: #6f8af9;
    font-size: 12px;
    font-weight: 500;
    line-height: 18px;
    padding: 2px 6px;

    &.tag-offLine {
      background: #e5f3ff;
      color: #42a4e5;
    }
  }
}
.timeline-main {
  border-radius: 8px;
  background: #fff;
  padding: 8px;
}
.timeline-item {
  position: relative;
  display: flex;
  align-items: stretch;
  margin-bottom: 12px;

  .card {
    position: relative;
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .status {
      font-size: 15px;
      font-weight: 500;
      padding: 4px 8px;
      border-radius: 6px;
    }
    .date {
      color: #849ab2;
      font-size: 14px;
    }
    .meta {
      display: flex;
      align-items: center;
      width: 100px;
      .round {
        color: #3f5b7d;
        font-size: 14px;
      }
    }
  }
}
.timeline-item:last-child {
  margin-bottom: 0;
}
</style>
